import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useLocalized } from '../hooks/useLocalized';
import { useCookieConsent } from '../context/CookieConsentContext';
import api from '../utils/api';

const FooterContainer = styled.footer<{ $noPaddingTop?: boolean; $hideMainSection?: boolean }>`
  background: ${props => props.$hideMainSection ? 'transparent' : '#f7f3eb'};
  color: #333;
  padding: ${props => props.$hideMainSection ? '0' : props.$noPaddingTop ? '0' : '3rem 0 0'};
  margin-top: auto;
  position: relative;
  border-top: ${props => props.$hideMainSection ? 'none' : '1px solid #e8e6e3'};
`;

const FooterContent = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 4rem;

  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }
`;

const FooterMain = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 3fr;
  gap: 6rem;
  margin-bottom: 3rem;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const BrandSection = styled.div`
  h2 {
    font-family: 'Miramar', 'Montserrat', 'Futura PT', -apple-system, BlinkMacSystemFont, sans-serif;
    font-size: 1.5rem;
    font-weight: 500;
    letter-spacing: 0.2em;
    margin-bottom: 1.5rem;
    color: #333;
    text-transform: uppercase;
  }

  p {
    font-size: 0.875rem;
    color: #666;
    line-height: 1.8;
    max-width: 400px;
  }
`;

const Newsletter = styled.div`
  h4 {
    font-size: 0.75rem;
    font-weight: 400;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    margin-bottom: 1.5rem;
    color: #333;
  }

  p {
    font-size: 0.875rem;
    color: #666;
    margin-bottom: 1.5rem;
    line-height: 1.6;
  }
`;

const NewsletterForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 100%;
`;

const NewsletterInput = styled.input`
  flex: 1;
  padding: 0.875rem 1.25rem;
  background: #ffffff;
  border: 1px solid #d4d2cf;
  color: #333;
  font-size: 16px; /* Prevent zoom on iOS */
  font-weight: 300;
  letter-spacing: 0.02em;

  &::placeholder {
    color: #999;
  }

  &:focus {
    outline: none;
    border-color: #8b7355;
    background: #ffffff;
  }

  @media (min-width: 768px) {
    font-size: 0.875rem; /* Smaller font on desktop */
  }
`;

const NewsletterButton = styled.button`
  padding: 0.875rem 2rem;
  background: #280212;
  border: 1px solid #280212;
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 400;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  cursor: pointer;
  width: 100%;
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1.5fr;
  gap: 5rem;

  @media (max-width: 968px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 3rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const FooterSection = styled.div`
  h4 {
    font-size: 0.75rem;
    font-weight: 400;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    margin-bottom: 1.5rem;
    color: #333;
  }

  ul {
    list-style: none;
    padding: 0;

    li {
      margin-bottom: 0.75rem;

      a {
        color: #666;
        font-size: 0.8125rem;
        font-weight: 300;
        letter-spacing: 0.01em;
        transition: all 0.2s ease;
        line-height: 1.5;

        &:hover {
          color: #8b7355;
        }
      }
    }
  }
`;

const FooterBottom = styled.div<{ $hideMainSection?: boolean }>`
  background: #280212;
  border-top: 1px solid #280212;
  width: 100%;
  margin-top: ${props => props.$hideMainSection ? '0' : '2rem'};
`;

const FooterBottomContent = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  padding: 2rem 4rem;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
    text-align: center;
    padding: 2rem 1.5rem;
  }
`;

const Logo = styled.div`
  font-family: 'Miramar', 'Montserrat', 'Futura PT', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 1.3rem;
  font-weight: 500;
  letter-spacing: 0.2em;
  color: #ffffff;
`;

const Copyright = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.75rem;
  font-weight: 300;
  letter-spacing: 0.05em;
  margin: 0;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1.5rem;

  a {
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(255, 255, 255, 0.7);
    transition: all 0.2s ease;

    &:hover {
      color: #ffffff;
    }

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

// Simplified footer for cart page
const SimpleFooterContainer = styled.footer`
  background: #280212;
  color: #ffffff;
  padding: 2rem 0;
  margin-top: auto;
  position: relative;
  border-top: 1px solid #280212;
  z-index: 1;

  @media (max-width: 768px) {
    display: none;
  }
`;

const SimpleFooterContent = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 4rem;

  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }
`;

const SimpleFooterBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
`;

interface FooterLuxuryProps {
  hideMainSection?: boolean;
  noPaddingTop?: boolean;
}

const FooterLuxury: React.FC<FooterLuxuryProps> = ({ hideMainSection = false, noPaddingTop = false }) => {
  const location = useLocation();
  const isCartPage = location.pathname === '/cart';
  const [email, setEmail] = useState('');
  const { getLocalized } = useLocalized();
  const { openSettings } = useCookieConsent();
  const [subscribeStatus, setSubscribeStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [subscribeMessage, setSubscribeMessage] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setSubscribeStatus('error');
      setSubscribeMessage(getLocalized('Lütfen geçerli bir e-posta adresi girin', 'Please enter a valid email address'));
      return;
    }

    setSubscribeStatus('loading');

    try {
      const response = await api.post('/newsletter/subscribe', { email });

      setSubscribeStatus('success');
      setSubscribeMessage(response.data.message || getLocalized('Başarıyla abone oldunuz! Teşekkür ederiz.', 'Successfully subscribed! Thank you.'));
      setEmail('');

      setTimeout(() => {
        setSubscribeStatus('idle');
        setSubscribeMessage('');
      }, 3000);
    } catch (error: unknown) {
      const message = (error as any)?.response?.data?.message
        || (error instanceof Error ? error.message : getLocalized('Bir hata oluştu. Lütfen tekrar deneyin.', 'An error occurred. Please try again.'));
      setSubscribeStatus('error');
      setSubscribeMessage(message);

      setTimeout(() => {
        setSubscribeStatus('idle');
        setSubscribeMessage('');
      }, 3000);
    }
  };

  if (isCartPage) {
    return (
      <SimpleFooterContainer>
        <SimpleFooterContent>
          <SimpleFooterBottom>
            <SocialLinks>
              <a href="https://www.instagram.com/valevenio/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              {/* TikTok - hidden for now
              <a href="#" aria-label="TikTok">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
              */}
            </SocialLinks>
            <Copyright>{getLocalized(`© ${new Date().getFullYear()} VALEVENIO. Tüm hakları saklıdır.`, `© ${new Date().getFullYear()} VALEVENIO. All rights reserved.`)}</Copyright>
          </SimpleFooterBottom>
        </SimpleFooterContent>
      </SimpleFooterContainer>
    );
  }

  return (
    <FooterContainer $noPaddingTop={noPaddingTop} $hideMainSection={hideMainSection}>
      {!hideMainSection && (
        <FooterContent>
          <FooterMain>
            <BrandSection>
              <h2>VALEVENIO</h2>
              <p>
                {getLocalized(
                  'Günlük hayata eşlik eden, zamansız tasarımlara sahip özel parçalar.',
                  'Distinct pieces designed to accompany everyday life with timeless presence.'
                )}
              </p>
            </BrandSection>

            <FooterGrid>
              <FooterSection>
                <h4>{getLocalized('Kurumsal', 'The Brand')}</h4>
                <ul>
                  <li><Link to="/">{getLocalized('Ürünler', 'Shop')}</Link></li>
                  <li><Link to="/craft-behind">The Craft Behind</Link></li>
                </ul>
              </FooterSection>

              <FooterSection>
                <h4>{getLocalized('MÜŞTERİ HİZMETLERİ', 'CUSTOMER CARE')}</h4>
                <ul>
                  <li><Link to="/contact">{getLocalized('İletişim', 'Contact')}</Link></li>
                  <li><Link to="/returns">{getLocalized('İade & Değişim', 'Returns & Exchange')}</Link></li>
                  <li><Link to="/shipping">{getLocalized('Kargo ve Teslimat', 'Shipping & Delivery')}</Link></li>
                  <li><Link to="/track-order">{getLocalized('Sipariş Takibi', 'Order Tracking')}</Link></li>
                  <li><Link to="/privacy-policy">{getLocalized('Gizlilik Politikası', 'Privacy Policy')}</Link></li>
                  <li><Link to="/terms-of-use">{getLocalized('Kullanım Şartları', 'Terms of Use')}</Link></li>
                  <li><button onClick={openSettings} style={{ background: 'none', border: 'none', padding: 0, margin: 0, cursor: 'pointer', color: '#666', fontSize: '0.8125rem', fontWeight: 300, letterSpacing: '0.01em', lineHeight: 1.5, fontFamily: 'inherit', textAlign: 'left' }}>{getLocalized('Çerez Ayarları', 'Cookie Settings')}</button></li>
                </ul>
              </FooterSection>

              <Newsletter>
                <h4>Newsletter</h4>
                <p>{getLocalized('Özel teklifler almak için bültenimize kaydolun.', 'Sign up to receive exclusive offers.')}</p>
                <NewsletterForm onSubmit={handleNewsletterSubmit}>
                  <label htmlFor="newsletter-email" style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0,0,0,0)' }}>
                    {getLocalized('E-posta adresi', 'Email address')}
                  </label>
                  <NewsletterInput
                    id="newsletter-email"
                    type="email"
                    placeholder="E-mail"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={subscribeStatus === 'loading'}
                  />
                  <NewsletterButton type="submit" disabled={subscribeStatus === 'loading'} aria-label={getLocalized('Bültene abone ol', 'Subscribe to newsletter')}>
                    {subscribeStatus === 'loading' ? '...' : getLocalized('Abone Ol', 'Subscribe')}
                  </NewsletterButton>
                </NewsletterForm>
                {subscribeMessage && (
                  <p style={{
                    marginTop: '1rem',
                    fontSize: '0.75rem',
                    color: subscribeStatus === 'success' ? '#4caf50' : '#f44336'
                  }}>
                    {subscribeMessage}
                  </p>
                )}
              </Newsletter>
            </FooterGrid>
          </FooterMain>
        </FooterContent>
      )}

      <FooterBottom $hideMainSection={hideMainSection}>
        <FooterBottomContent>
          <SocialLinks>
            <a href="https://www.instagram.com/valevenio/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
            {/* TikTok - hidden for now
            <a href="#" aria-label="TikTok">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
              </svg>
            </a>
            */}
          </SocialLinks>
          <Copyright>{getLocalized(`© ${new Date().getFullYear()} VALEVENIO. Tüm hakları saklıdır.`, `© ${new Date().getFullYear()} VALEVENIO. All rights reserved.`)}</Copyright>
        </FooterBottomContent>
      </FooterBottom>
    </FooterContainer>
  );
};

export default FooterLuxury;
